/**
 * F2 — Meeting SCHEDULE (ordered blocks).
 *
 * Shared pure engine (packages/shared/src/schedule — adaptation of block
 * engine from aula): stable order, accumulated timeline anchored to
 * meeting start time, and reordering with dense renumbering. Block
 * types are a PLUGGABLE registry (kinds.ts): adding one does not touch engine.
 * `responsible_name` stamped by server (write-path name_stamp).
 */
import { msg } from "@lingui/core/macro";
import { type Accessor, createMemo, createSignal, For, Show } from "solid-js";
import {
  changedAssignments,
  computeTimeline,
  defaultScheduleKinds,
  getPeopleQuery,
  getScheduleBlocksQuery,
  moveBlock,
  orderScheduleBlocks,
  parseInstant,
  patchRowVerified,
  queryKeys,
  totalDurationMinutes,
} from "@agape/shared";
import {
  eq,
  type MeetingRecord,
  type PersonRecord,
  schedule_block,
  type ScheduleBlockRecord,
} from "@agape/powersync";
import { ArrowDown, ArrowUp, CalendarClock, Plus, Trash2 } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog, TextArea } from "./form";
import { newId, nowIso, useDomainQuery, useWriter } from "../use-domain";

const KIND_REGISTRY = defaultScheduleKinds();

export function ScheduleSection(props: {
  meetingId: string;
  meeting: Accessor<MeetingRecord | undefined>;
  canWrite: Accessor<boolean>;
}) {
  const { t } = useI18n();
  const write = useWriter();

  const blocks = useDomainQuery<ScheduleBlockRecord>(
    (uid) => queryKeys.domain.scheduleBlocks(uid),
    (db) => getScheduleBlocksQuery(db),
  );
  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );

  const kindLabel = (kind: string | null) => {
    switch (kind) {
      case "worship": {
        return t(msg`Worship / music`);
      }
      case "sermon": {
        return t(msg`Sermon`);
      }
      case "prayer": {
        return t(msg`Prayer`);
      }
      case "reading": {
        return t(msg`Reading`);
      }
      case "announcement": {
        return t(msg`Announcements`);
      }
      case "offering": {
        return t(msg`Offering`);
      }
      case "transition": {
        return t(msg`Transition`);
      }
      default: {
        return t(msg`Other`);
      }
    }
  };

  /** SERIES schedule (occurrence_start NULL), ordered. */
  const ordered = createMemo(() =>
    orderScheduleBlocks(
      (blocks.data ?? []).filter((b) => b.meeting_id === props.meetingId && !b.occurrence_start),
    ),
  );
  const meetingStartMs = () => parseInstant(props.meeting()?.starts_at ?? null);
  const timeline = createMemo(() => computeTimeline(ordered(), meetingStartMs()));
  const totalMinutes = () => totalDurationMinutes(ordered());

  const timeFormat = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const blockTime = (startMs: number | null, offsetMinutes: number) =>
    startMs === null ? `+${offsetMinutes}′` : timeFormat.format(new Date(startMs));

  // ── Alta ──────────────────────────────────────────────────────────────────
  const [open, setOpen] = createSignal(false);
  const [kind, setKind] = createSignal("worship");
  const [title, setTitle] = createSignal("");
  const [duration, setDuration] = createSignal("");
  const [responsible, setResponsible] = createSignal("");
  const [notes, setNotes] = createSignal("");
  const openAdd = () => {
    setKind("worship");
    setTitle("");
    setDuration(String(KIND_REGISTRY.defaultDuration("worship")));
    setResponsible("");
    setNotes("");
    setOpen(true);
  };
  const onKindChange = (value: string) => {
    setKind(value);
    setDuration(String(KIND_REGISTRY.defaultDuration(value)));
  };
  const addBlock = async () => {
    const minutes = Number.parseInt(duration(), 10);
    if (!title().trim() || Number.isNaN(minutes) || minutes < 0) {
      return;
    }
    await write()
      .insert(schedule_block)
      .values({
        id: newId(),
        meeting_id: props.meetingId,
        church_id: props.meeting()?.church_id,
        kind: kind(),
        title: title().trim(),
        duration_minutes: minutes,
        responsible_person_id: responsible() || null,
        notes: notes().trim() || null,
        sort_order: ordered().length + 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setOpen(false);
    showToast(t(msg`Block added to the schedule`), "success");
  };

  // ── Reordenar / borrar ────────────────────────────────────────────────────
  const move = async (id: string, direction: "up" | "down") => {
    const assignments = moveBlock(ordered(), id, direction);
    if (!assignments) {
      return;
    }
    try {
      for (const change of changedAssignments(ordered(), assignments)) {
        await patchRowVerified(write(), schedule_block, schedule_block.id, change.id, {
          sort_order: change.sortOrder,
        });
      }
    } catch {
      showToast(t(msg`Couldn't reorder the schedule — try again`), "error");
    }
  };
  const remove = async (id: string) => {
    await write().delete(schedule_block).where(eq(schedule_block.id, id));
  };

  return (
    <section class="space-y-2">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t(msg`Schedule`)}
          <Show when={ordered().length > 0}>
            {" "}
            <span data-testid="schedule-total">
              · {totalMinutes()} {t(msg`min`)}
            </span>
          </Show>
        </h3>
        <Show when={props.canWrite()}>
          <Button size="sm" variant="ghost" data-testid="schedule-add" onClick={openAdd}>
            <Plus size={14} /> {t(msg`Add block`)}
          </Button>
        </Show>
      </div>
      <Show
        when={timeline().length > 0}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="schedule-empty">
            {t(msg`No schedule yet.`)}
          </p>
        }
      >
        <div class="space-y-2" data-testid="schedule-list">
          <For each={timeline()}>
            {(entry, index) => (
              <Card data-testid="schedule-item" data-block-id={entry.block.id}>
                <CardContent class="flex items-center justify-between gap-3 py-2.5">
                  <div class="flex min-w-0 items-center gap-3">
                    <span
                      class="shrink-0 rounded-md bg-accent/10 px-2 py-1 font-mono text-xs"
                      data-testid="schedule-time"
                    >
                      {blockTime(entry.startMs, entry.offsetMinutes)}
                    </span>
                    <div class="min-w-0">
                      <p class="truncate text-sm font-medium" data-testid="schedule-item-title">
                        {entry.block.title}
                      </p>
                      <p class="truncate text-xs text-muted-foreground">
                        {[
                          kindLabel(entry.block.kind),
                          `${entry.durationMinutes} ${t(msg`min`)}`,
                          entry.block.responsible_name,
                          entry.block.notes,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                  </div>
                  <Show when={props.canWrite()}>
                    <div class="flex shrink-0 gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t(msg`Move block up`)}
                        data-testid="schedule-move-up"
                        disabled={index() === 0}
                        onClick={() => void move(entry.block.id, "up")}
                      >
                        <ArrowUp size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t(msg`Move block down`)}
                        data-testid="schedule-move-down"
                        disabled={index() === timeline().length - 1}
                        onClick={() => void move(entry.block.id, "down")}
                      >
                        <ArrowDown size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t(msg`Remove block`)}
                        data-testid="schedule-remove"
                        onClick={() => void remove(entry.block.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Show>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </Show>

      <FormDialog
        open={open()}
        onOpenChange={setOpen}
        title={t(msg`Add schedule block`)}
        icon={<CalendarClock size={20} />}
        submitTestId="schedule-submit"
        disabled={!title().trim() || !duration()}
        onSubmit={addBlock}
      >
        <Field id="sch-kind" label={t(msg`Type`)}>
          <Select
            options={KIND_REGISTRY.ids().map((id) => ({
              value: id,
              label: kindLabel(id),
            }))}
            value={kind()}
            onChange={onKindChange}
            aria-label={t(msg`Type`)}
          />
        </Field>
        <Field
          id="sch-title"
          label={t(msg`Title`)}
          value={title()}
          onInput={setTitle}
          testId="schedule-title-input"
        />
        <Field
          id="sch-duration"
          label={t(msg`Duration (minutes)`)}
          type="number"
          value={duration()}
          onInput={setDuration}
          testId="schedule-duration-input"
        />
        <Field id="sch-resp" label={t(msg`Responsible`)}>
          <Select
            options={(people.data ?? []).map((p) => ({
              value: p.id,
              label: p.name ?? p.id,
            }))}
            value={responsible()}
            onChange={setResponsible}
            aria-label={t(msg`Responsible`)}
            emptyLabel={t(msg`No people synced`)}
          />
        </Field>
        <Field id="sch-notes" label={t(msg`Notes`)}>
          <TextArea id="sch-notes" rows={2} value={notes()} onInput={setNotes} class="font-sans" />
        </Field>
      </FormDialog>
    </section>
  );
}
